import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import ExpensesPage from './pages/ExpensesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import AskAIPage from './pages/AskAIPage';
import InstallPrompt from './components/InstallPrompt';
import { AuthProvider, useAuth } from './context/AuthContext';
import { initGoogleAuth, isNativePlatform } from './utils/nativeAuth';
import { initNativePushNotifications } from './utils/nativePush';
import { App as CapApp } from '@capacitor/app';

// RTL Direction Handler
function DirectionHandler({ children }) {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    const dir = i18n.language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  
  return children;
}

// Native App Initializer - handles Capacitor-specific setup
function NativeAppInitializer({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Auth for native platforms
    if (isNativePlatform()) {
      initGoogleAuth();
    }
  }, []);

  useEffect(() => {
    // Initialize push notifications after user is authenticated
    if (user && isNativePlatform()) {
      initNativePushNotifications();
    }
  }, [user]);

  useEffect(() => {
    // Handle deep links for home screen widget
    if (isNativePlatform()) {
      CapApp.addListener('appUrlOpen', (event) => {
        console.log('[App] Deep link received:', event.url);
        if (event.url.includes('shkalot://record')) {
          // Navigate to dashboard with auto-record flag
          navigate('/dashboard?autoRecord=true');
        }
      });
    }

    return () => {
      if (isNativePlatform()) {
        CapApp.removeAllListeners();
      }
    };
  }, [navigate]);

  // Check for autoRecord query param on dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard' && location.search.includes('autoRecord=true')) {
      // The Dashboard component will handle this
      console.log('[App] Auto-record mode activated from deep link');
    }
  }, [location]);

  return children;
}

// Helper to check if we're on the app subdomain
const isAppSubdomain = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'app.shkalot.xyz';
};

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-light">
        <img 
          src="/icon-192.png" 
          alt="Shkalot" 
          className="w-16 h-16 mb-4 animate-pulse" 
        />
        <div className="text-xl font-bold text-gray-600">טוען...</div>
      </div>
    );
  }

  if (!user) {
    // If on app subdomain and not logged in, redirect to main domain
    if (isAppSubdomain()) {
      window.location.href = 'https://shkalot.xyz';
      return null;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <DirectionHandler>
        <Router>
          <NativeAppInitializer>
            <Toaster
            position="top-center"
            toastOptions={{
              duration: 2000,
              style: {
                border: '4px solid #000',
                padding: '16px',
                fontWeight: 'bold',
              },
              success: {
                style: {
                  background: '#00FF00',
                },
              },
              error: {
                style: {
                  background: '#FF5555',
                  color: '#fff',
                },
              },
            }}
          />
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LandingPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <ExpensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ask"
              element={
                <ProtectedRoute>
                  <AskAIPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          </NativeAppInitializer>
        </Router>
      </DirectionHandler>
    </AuthProvider>
  );
}

export default App;
